import React, { useState, useRef, useEffect } from 'react';
import './App.css';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://langchain-simple-worker.terry085122.workers.dev' 
  : 'http://localhost:8787';

function App() {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState('gpt-5');
  const [availableModels, setAvailableModels] = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    // Load available models
    fetchModels();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchModels = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/models`);
      const data = await response.json();
      if (data.models) {
        setAvailableModels(data.models);
      }
    } catch (error) {
      console.error('Failed to fetch models:', error);
      // Fallback models
      setAvailableModels([
        { 
          id: 'gpt-5', 
          name: 'GPT-5', 
          provider: 'OpenAI',
          description: 'Advanced language model from OpenAI'
        },
        { 
          id: 'gemini-2.5-flash', 
          name: 'Gemini 2.5 Flash', 
          provider: 'Google',
          description: 'Fast and efficient model from Google'
        }
      ]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = { role: 'user', content: inputMessage, timestamp: new Date() };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: inputMessage,
          model: selectedModel
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      // Add assistant response
      const assistantMessage = { 
        role: 'assistant', 
        content: data.response,
        model: data.model,
        timestamp: new Date(data.timestamp)
      };
      
      setMessages(prev => [...prev, assistantMessage]);

    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = { 
        role: 'assistant', 
        content: `錯誤：${error.message || '無法連接到服務器，請稍後再試。'}`,
        isError: true,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([]);
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('zh-TW', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="app">
      <header className="app-header">
        <h1>🤖 LLM 簡單對話</h1>
        <div className="header-controls">
          <div className="model-selector">
            <label htmlFor="model-select">選擇模型：</label>
            <select 
              id="model-select"
              value={selectedModel} 
              onChange={(e) => setSelectedModel(e.target.value)}
              disabled={isLoading}
            >
              {availableModels.map(model => (
                <option key={model.id} value={model.id} title={model.description}>
                  {model.name} ({model.provider})
                </option>
              ))}
            </select>
          </div>
          <button onClick={clearChat} className="clear-button" disabled={isLoading}>
            清除對話
          </button>
        </div>
      </header>

      <main className="chat-container">
        <div className="messages">
          {messages.length === 0 && (
            <div className="welcome-message">
              <h2>歡迎使用 LLM 簡單對話！</h2>
              <p>選擇一個 AI 模型並開始對話吧。</p>
            </div>
          )}
          
          {messages.map((message, index) => (
            <div key={index} className={`message ${message.role}`}>
              <div className="message-header">
                <strong>{message.role === 'user' ? '👤 您' : '🤖 AI'}</strong>
                {message.model && <span className="model-tag">{message.model}</span>}
                <span className="timestamp">{formatTimestamp(message.timestamp)}</span>
              </div>
              <div className={`message-content ${message.isError ? 'error' : ''}`}>
                {message.content}
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="message assistant">
              <div className="message-header">
                <strong>🤖 AI</strong>
                <span className="loading-indicator">思考中...</span>
              </div>
              <div className="message-content loading">
                <div className="loading-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        <form onSubmit={handleSubmit} className="input-form">
          <div className="input-container">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="輸入您的問題..."
              disabled={isLoading}
              className="message-input"
            />
            <button 
              type="submit" 
              disabled={isLoading || !inputMessage.trim()}
              className="send-button"
            >
              {isLoading ? '⏳ 等待中' : '📤 發送'}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}

export default App;